import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  alpha,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import axiosInstance from '../../../lib/axios';
import { CustomerType, type Customer, type CreateCustomerDto } from '../../../types/customer';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface QuickCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: Customer) => void;
  colors: POSColors;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
  email?: string;
}

export const QuickCustomerModal: React.FC<QuickCustomerModalProps> = ({
  open,
  onClose,
  onCustomerCreated,
  colors,
}) => {
  // Customer type selector
  const [customerType, setCustomerType] = useState<CustomerType>(CustomerType.INDIVIDUAL);

  // Individual fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Business fields
  const [companyName, setCompanyName] = useState('');

  // Common fields
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Form state
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setCustomerType(CustomerType.INDIVIDUAL);
      setFirstName('');
      setLastName('');
      setCompanyName('');
      setPhone('');
      setEmail('');
      setErrors({});
      setApiError(null);
      setIsLoading(false);
    }
  }, [open]);

  // Handle type change - reset type-specific fields
  const handleTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: CustomerType | null) => {
    if (newType !== null) {
      setCustomerType(newType);
      setErrors({});
      // Reset type-specific fields
      if (newType === CustomerType.INDIVIDUAL) {
        setCompanyName('');
      } else {
        setFirstName('');
        setLastName('');
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (customerType === CustomerType.INDIVIDUAL) {
      if (!firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
    } else {
      if (!companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
    }

    // Phone is optional, but validate format if provided
    if (phone.trim()) {
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
        newErrors.phone = 'Invalid phone format';
      }
    }

    // Email is required
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      // Build request data based on customer type
      const requestData: CreateCustomerDto = {
        type: customerType,
        email: email.trim(),
        phone: phone.trim() || undefined,
      };

      if (customerType === CustomerType.INDIVIDUAL) {
        requestData.firstName = firstName.trim();
        requestData.lastName = lastName.trim();
      } else {
        requestData.companyName = companyName.trim();
        requestData.isApproved = true; // Auto-approve for POS quick create
      }

      const response = await axiosInstance.post('/customers', requestData);

      const newCustomer = response.data;
      onCustomerCreated(newCustomer);
      onClose();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to create customer';
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: alpha(colors.accent, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonAddIcon sx={{ color: colors.accent, fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Create Customer
        </Typography>
      </DialogTitle>

      <DialogContent>
        {apiError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: alpha(colors.accent, 0.1),
              color: colors.textPrimary,
              '& .MuiAlert-icon': {
                color: '#EF4444',
              },
            }}
          >
            {apiError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Customer Type Selector */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textSecondary,
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Customer Type
            </Typography>
            <ToggleButtonGroup
              value={customerType}
              exclusive
              onChange={handleTypeChange}
              fullWidth
              disabled={isLoading}
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px !important',
                  mx: 0.5,
                  '&:first-of-type': {
                    ml: 0,
                  },
                  '&:last-of-type': {
                    mr: 0,
                  },
                  '&.Mui-selected': {
                    backgroundColor: alpha(colors.accent, 0.15),
                    borderColor: colors.accent,
                    color: colors.accent,
                    '&:hover': {
                      backgroundColor: alpha(colors.accent, 0.25),
                    },
                  },
                  '&:not(.Mui-selected)': {
                    backgroundColor: colors.surfaceLight,
                    color: colors.textSecondary,
                    '&:hover': {
                      backgroundColor: alpha(colors.textMuted, 0.1),
                    },
                  },
                },
              }}
            >
              <ToggleButton value={CustomerType.INDIVIDUAL}>
                <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                Individual
              </ToggleButton>
              <ToggleButton value={CustomerType.BUSINESS}>
                <BusinessIcon sx={{ mr: 1, fontSize: 18 }} />
                Business
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Individual-specific fields */}
          {customerType === CustomerType.INDIVIDUAL && (
            <>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                size="small"
                required
                disabled={isLoading}
                sx={getTextFieldStyles(colors)}
              />

              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                size="small"
                required
                disabled={isLoading}
                sx={getTextFieldStyles(colors)}
              />
            </>
          )}

          {/* Business-specific fields */}
          {customerType === CustomerType.BUSINESS && (
            <TextField
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
              fullWidth
              size="small"
              required
              disabled={isLoading}
              sx={getTextFieldStyles(colors)}
            />
          )}

          {/* Common fields */}
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            placeholder="+213551234567"
            fullWidth
            size="small"
            disabled={isLoading}
            sx={getTextFieldStyles(colors)}
          />

          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="customer@example.com"
            type="email"
            fullWidth
            size="small"
            required
            disabled={isLoading}
            sx={getTextFieldStyles(colors)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            '&:hover': {
              borderColor: colors.textMuted,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentLight,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(colors.accent, 0.5),
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: '#FFF' }} />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function for text field styles
const getTextFieldStyles = (colors: POSColors) => ({
  '& .MuiInputLabel-root': {
    color: colors.textMuted,
    '&.Mui-focused': {
      color: colors.accent,
    },
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceLight,
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.textMuted,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accent,
    },
    '&.Mui-error fieldset': {
      borderColor: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: colors.textPrimary,
  },
  '& .MuiFormHelperText-root': {
    color: colors.textMuted,
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
});

export default QuickCustomerModal;
